import React, { useState, useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import FileViewer from 'molecules/FileViewer/FileViewer'
import { useTranslationMapDetailsContext } from '../../contexts/TranslationMapDetailsContext'

export interface ArtifactEditorProps {
  disabled?: boolean
}

const ArtifactEditor: React.FC<ArtifactEditorProps> = ({ disabled = false }) => {
  const {
    translationMap,
    currentArtifactValue,
    updateArtifactValue,
  } = useTranslationMapDetailsContext()

  // Local state for smooth typing experience
  const [localArtifactValue, setLocalArtifactValue] = useState(currentArtifactValue)
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Sync local state with context when it changes
  useEffect(() => {
    setLocalArtifactValue(currentArtifactValue)
  }, [currentArtifactValue])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }
    }
  }, [])

  const handleArtifactChange = (newValue: string) => {
    // Update local state immediately for smooth typing
    setLocalArtifactValue(newValue)

    // Clear previous timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current)
    }

    // Debounce context update to prevent re-renders during typing
    updateTimeoutRef.current = setTimeout(() => {
      updateArtifactValue(newValue)
    }, 500)
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: '400px',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, primary.main, primary.light)',
          opacity: 0.6,
          zIndex: 1,
        },
      }}
    >
      <FileViewer
        key={`artifact-${translationMap?.id}`}
        content={localArtifactValue}
        contentType="python"
        showOptions={false}
        options={{ heigth: '530px', readonly: disabled }}
        onChange={handleArtifactChange}
      />
    </Box>
  )
}

export default ArtifactEditor
